import React, { useEffect, useState } from "react";
import _ from "lodash";
import { Button } from "@showitinc/showit-wc";

import styles from "./SiteSettingsModal.module.css";
import BlogDomainSelector from "../../../components/BlogDomainSelector";
import ControlLabel from "../../../components/base/ControlLabel";
import EditableValue from "../../../components/base/EditableValue";
import SiteSettingsActions from "../../../actions/SiteSettingsActions";
import { VIEW_BLOG } from "../../../stores/SiteSettingsStore";
import AppConstants from "../../../constants/AppConstants";
import SubscribeButton from "../../../components/SubscribeButton";
import UIActions from "../../../actions/UIActions";
import uiPrefix from "../../../utils/UiPrefixUtils";
import { showDomainSetupModal } from "../../../adaptors/ModalAdaptors";
import {
  loadDraft,
  subscribeToDraftChanges,
} from "../../../components/domainsetup/draftStorage";

const BlogSettings = ({ siteSettings, onChange, user }) => {
  const allowedBlogType = user.get("allowedBlogType");
  const blogSetupNeeded = user.get("blogSetupNeeded");
  const blogStatus = user.get("blogStatus");
  const primaryDomain = user.get("domain");
  const userId = user.get("user_id");
  const blogId = user.get("blogId");

  // Live-track an in-progress domain setup draft. The DomainSetupWizard only
  // writes a draft once the migration path has passed the first /setup POST,
  // so any draft we see here represents a real "stuck before submitting
  // migration details" state. Once user.blogId is set, the migration request
  // has been submitted and there's nothing left to resume.
  const [draft, setDraft] = useState(() => (blogId ? null : loadDraft(userId)));
  useEffect(() => {
    const recompute = () => {
      setDraft(blogId ? null : loadDraft(userId));
    };
    recompute();
    return subscribeToDraftChanges(recompute);
  }, [userId, blogId]);
  const blogDomain = siteSettings.getIn(["site", "blog_domain"], false);
  const isSubscribed = siteSettings.get("isSubscribed");
  const wpHostId = siteSettings.getIn(["site", "wp_host_id"]);
  const currentView = siteSettings.get("view");

  const rowStyle = {
    display: "flex",
    paddingTop: 8,
    paddingBottom: 8,
    justifyContent: "center",
  };

  let headerText = "";
  let controls = null;
  let setup = null;

  const _getBlogUpdateControls = (overrideDomain) => (
    <>
      <ControlLabel style={{ width: "21%" }}>Blog Domain</ControlLabel>
      <EditableValue
        value={overrideDomain || blogDomain}
        onClick={() => SiteSettingsActions.changeView(VIEW_BLOG)}
      />
    </>
  );

  const _getWordpressMessage = () => {
    const openBlogSetup = () =>
      UIActions.modalOpen(AppConstants.MODAL_BLOG_SETUP, {
        onHide: () => UIActions.modalClose(AppConstants.MODAL_BLOG_SETUP),
      });

    let buttonText = "Setup Your Blog";
    let setupMessage = (
      <>Your subscription includes a blog. Set up your blog to link to your design.</>
    );
    if (
      blogStatus === AppConstants.BLOG_STATUS_AWAITING ||
      blogStatus === AppConstants.BLOG_STATUS_PROCESSING
    ) {
      buttonText = "Update Request";
      setupMessage = (
        <>We are currently setting up your Showit Blog. We will contact you when it is ready.</>
      );
    }

    return {
      message: setupMessage,
      button: (
        <Button
          look="primary"
          data-e2e={uiPrefix("modal-open-blog-setup-btn")}
          className={uiPrefix("modal-open-blog-setup-btn")}
          onClick={openBlogSetup}
        >
          {buttonText}
        </Button>
      ),
    };
  };

  const _getDomainSetupResumeMessage = () => ({
    message: (
      <>Your blog migration is in progress. Finish entering your migration details to submit your request.</>
    ),
    button: (
      <Button
        look="primary"
        data-e2e={uiPrefix("resume-domain-setup-btn")}
        className={uiPrefix("resume-domain-setup-btn")}
        onClick={showDomainSetupModal}
      >
        Finish Migration Request
      </Button>
    ),
  });

  if (_.isNumber(wpHostId)) {
    headerText = <>Your site design can be linked to one WordPress blog.</>;
    controls = _getBlogUpdateControls(blogDomain);
    rowStyle.justifyContent = undefined;
  } else {
    if (isSubscribed) {
      if (allowedBlogType === AppConstants.BLOG_TYPE_NONE || _.isEmpty(allowedBlogType)) {
        headerText = (
          <>
            Your subscription does not include a blog.
            <br />
            Please contact Showit Support to upgrade your subscription.
          </>
        );
      } else if (_.isEmpty(primaryDomain)) {
        headerText = "Please add your domain to your account.";
        controls = (
          <Button
            look="primary"
            data-e2e={uiPrefix("modal-domain-setup-open-btn")}
            className={uiPrefix("modal-domain-setup-open-btn")}
            onClick={showDomainSetupModal}
          >
            Add Your Domain
          </Button>
        );
      } else {
        if (draft) {
          // Resume an interrupted migration before any blog-setup CTA — the
          // user is mid-flow on the domain setup wizard and needs to finish
          // there first.
          const { message, button } = _getDomainSetupResumeMessage();
          setup = (
            <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 16 }}>
              <p className="instrux">{message}</p>
              <div style={{ display: "flex", justifyContent: "center" }}>{button}</div>
            </div>
          );
        } else if (blogSetupNeeded) {
          const { message, button } = _getWordpressMessage(blogStatus);
          setup = (
            <div style={{ borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 16 }}>
              <p className="instrux">{message}</p>
              <div style={{ display: "flex", justifyContent: "center" }}>{button}</div>
            </div>
          );
        }

        headerText = <>Link this site design to your WordPress blog.</>;
        controls = _getBlogUpdateControls("(None)");
        rowStyle.justifyContent = undefined;
      }
    } else {
      headerText =
        "Design your WordPress theme in Showit. Choose from two subscription options to get started.";
      controls = <SubscribeButton>Subscribe Now</SubscribeButton>;
    }
  }

  return (
    <div className={styles.blogSettings}>
      {currentView === VIEW_BLOG && (
        <BlogDomainSelector
          propName="site.wp_host_id"
          height="24rem"
          value={wpHostId}
          onChange={onChange}
        />
      )}
      {currentView !== VIEW_BLOG && (
        <>
          {setup || null}
          <p className="instrux">{headerText}</p>
          <div style={rowStyle}>{controls}</div>
        </>
      )}
    </div>
  );
};

export default BlogSettings;
